import { FilterAlt } from "@mui/icons-material";
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TableSortLabel, TextField } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { getLoading } from "../../pages/execution-events/ExecutionEvents";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskCompanyDTO, RiskEpiDeliveryDTO } from "../../types/RiskAssessmentDTO";
import { countLabel } from "./riskEditorState";
import {
	DEFAULT_DELIVERY_PERIOD, DEFAULT_DELIVERY_SORT, DELIVERY_PERIODS, DeliveryGroup, DeliveryPeriod, DeliverySort, DeliverySortColumn, deliveryOptions, filterDeliveries,
	formatDeliveryDate, groupDeliveries, toggleDeliverySort
} from "./riskEpiDeliveries";
import './RiskEpiDeliveriesModal.scss';

const ROW_HEIGHT = 40;
const EMPTY_VALUE = 'Não informado';
const COLUMNS: { id: DeliverySortColumn; label: string }[] = [
	{ id: 'date', label: 'Data de entrega' },
	{ id: 'sector', label: 'Setor' },
	{ id: 'position', label: 'Cargo' },
	{ id: 'epi', label: 'EPI' },
	{ id: 'ca', label: 'CA' },
	{ id: 'employees', label: 'Funcionários' }
];

type DeliveryLine = { group: DeliveryGroup; item?: RiskEpiDeliveryDTO };

interface Props {
	open: boolean;
	company: RiskCompanyDTO;
	onClose: () => void;
}

const RiskEpiDeliveriesModal: FC<Props> = ({ open, company, onClose }) => {
	const [deliveries, setDeliveries] = useState<RiskEpiDeliveryDTO[] | null>(null);
	const [loadedKey, setLoadedKey] = useState<string | null>(null);
	const [period, setPeriod] = useState<DeliveryPeriod>(DEFAULT_DELIVERY_PERIOD);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [sector, setSector] = useState<string | null>(null);
	const [position, setPosition] = useState<string | null>(null);
	const [sort, setSort] = useState<DeliverySort>(DEFAULT_DELIVERY_SORT);
	const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
	const requestKey = useRef<string | null>(null);
	const key = `${company.companyId}|${period.months}`;

	useEffect(() => {
		if (open && loadedKey !== key && requestKey.current !== key) {
			load();
		}
	}, [open, key]);

	useEffect(() => {
		scrollElement?.scrollTo({ top: 0 });
	}, [sector, position, sort, period]);

	const all = useMemo(() => deliveries || [], [deliveries]);
	const sectors = useMemo(() => deliveryOptions(all, 'sector', null), [all]);
	const positions = useMemo(() => deliveryOptions(all, 'position', sector), [all, sector]);
	const groups = useMemo(() => groupDeliveries(filterDeliveries(all, sector, position), sort),
		[all, sector, position, sort]);
	const lines = useMemo(() => groups.flatMap<DeliveryLine>(group => [{ group }, ...group.items.map(item => ({ group, item }))]),
		[groups]);
	const itemCount = lines.length - groups.length;
	const virtualizer = useVirtualizer({
		count: lines.length,
		getScrollElement: () => scrollElement,
		estimateSize: () => ROW_HEIGHT,
		overscan: 10,
		scrollMargin: ROW_HEIGHT
	});
	const items = virtualizer.getVirtualItems();
	const before = items.length ? items[0].start - ROW_HEIGHT : 0;
	const after = items.length ? virtualizer.getTotalSize() - (items[items.length - 1].end - ROW_HEIGHT) : 0;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			id="modal-risk-epi-deliveries"
			className="risk-epi-deliveries"
		>
			<DialogTitle>Entregas de EPI - {company.companyName} - {company.companyId}</DialogTitle>
			<DialogContent>
				<div className="filtros">
					<Autocomplete
						size="small"
						disableClearable
						options={DELIVERY_PERIODS}
						value={period}
						getOptionLabel={(option) => option.label}
						isOptionEqualToValue={(option, value) => option.months === value.months}
						onChange={(_, next) => setPeriod(next)}
						renderInput={(params) => <TextField {...params} label="Período"/>}
						sx={{ width: { xs: '100%', md: '200px' } }}
					/>
					<Autocomplete
						size="small"
						options={sectors}
						value={sector}
						getOptionLabel={(option) => option || EMPTY_VALUE}
						onChange={(_, next) => {
							setSector(next);
							setPosition(null);
						}}
						renderInput={(params) => <TextField {...params} label="Setor"/>}
						noOptionsText="Nenhum setor encontrado"
						sx={{ width: { xs: '100%', md: '300px' } }}
					/>
					<Autocomplete
						size="small"
						options={positions}
						value={position}
						getOptionLabel={(option) => option || EMPTY_VALUE}
						onChange={(_, next) => setPosition(next)}
						renderInput={(params) => <TextField {...params} label="Cargo"/>}
						noOptionsText="Nenhum cargo encontrado"
						sx={{ width: { xs: '100%', md: '300px' } }}
					/>
					<Button
						variant="outlined"
						startIcon={<FilterAlt/>}
						onClick={() => {
							setSector(null);
							setPosition(null);
						}}
					>
						Limpar filtros
					</Button>
					{!!deliveries && !loading && (
						<span className="contador">
							{countLabel(itemCount, 'item', 'itens')} em {countLabel(groups.length, 'data', 'datas')}
						</span>
					)}
				</div>
				{loading
					? getLoading()
					: failed
						? <div className="vazio">
							<span>Não foi possível carregar as entregas de EPI.</span>
							<Button variant="outlined" onClick={() => load()}>Tentar novamente</Button>
						</div>
						: !all.length
							? <div className="vazio">
								{period.months === null
									? 'Nenhuma entrega de EPI registrada no SGG para esta empresa.'
									: 'Nenhuma entrega de EPI registrada no SGG para esta empresa no período selecionado.'}
							</div>
							: !groups.length
								? <div className="vazio">Nenhuma entrega encontrada para os filtros aplicados.</div>
								: <div className="tabela" ref={setScrollElement}>
									<div className="linha cabecalho">
										{COLUMNS.map(column => (
											<div key={column.id} className={`celula ${column.id}`}>
												<TableSortLabel
													active={sort.column === column.id}
													direction={sort.column === column.id ? sort.direction : 'asc'}
													onClick={() => setSort(previous => toggleDeliverySort(previous, column.id))}
												>
													{column.label}
												</TableSortLabel>
											</div>
										))}
									</div>
									<div style={{ height: before }}/>
									{items.map(virtualItem => renderLine(lines[virtualItem.index], virtualItem.index))}
									<div style={{ height: after }}/>
								</div>
				}
			</DialogContent>
			<DialogActions>
				<Button id="risk-epi-deliveries-close-button" onClick={onClose}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);

	function renderLine(line: DeliveryLine, index: number) {
		const date = formatDeliveryDate(line.group.date);
		if (!line.item) {
			return (
				<div key={`data-${line.group.date}`} className="linha grupo" data-index={index}>
					<span className="data">{date}</span>
					<span>{countLabel(line.group.items.length, 'EPI entregue', 'EPIs entregues')}</span>
				</div>
			);
		}
		const item = line.item;
		return (
			<div key={`item-${index}`} className="linha" data-index={index}>
				<div className="celula date">{date}</div>
				<div className="celula sector" title={item.sector || EMPTY_VALUE}>{item.sector || EMPTY_VALUE}</div>
				<div className="celula position" title={item.position || EMPTY_VALUE}>{item.position || EMPTY_VALUE}</div>
				<div className="celula epi" title={item.epi}>{item.epi}</div>
				<div className="celula ca">{item.ca || '-'}</div>
				<div className="celula employees">{item.employees}</div>
			</div>
		);
	}

	async function load() {
		const current = key;
		requestKey.current = current;
		setLoading(true);
		setFailed(false);
		const response = await new RiskAssessmentService().getEpiDeliveries(company.companyId, period.months);
		if (requestKey.current !== current) {
			return;
		}
		requestKey.current = null;
		setDeliveries(response?.data || null);
		setFailed(!response);
		setLoadedKey(response ? current : null);
		setLoading(false);
	}
};

export default RiskEpiDeliveriesModal;
